import { makeAutoObservable, runInAction } from "mobx";
import { db } from "../db/db.js";
import { translations as staticFallbacks } from "../translations/localization.js";

const TRAN_PROJECT_ID = "proj-1768294094512";
const API_BASE = "https://localizationpro-api.tnl.one/api/v1";
const SECRET = process.env.API_KEY_TRANSLATIONS_TOOL_SECRET || "";
const CLIENT = process.env.API_KEY_TRANSLATIONS_TOOL_CLIENT || "";

const LOCALE_METADATA = {
  en: { label: 'English', flag: '🇬🇧' },
  it: { label: 'Italiano', flag: '🇮🇹' },
  fr: { label: 'Français', flag: '🇫🇷' },
  es: { label: 'Español', flag: '🇪🇸' },
  pt: { label: 'Português', flag: '🇵🇹' },
  ja: { label: '日本語', flag: '🇯🇵' },
  ko: { label: '한국어', flag: '🇰🇷' },
  zh: { label: '中文', flag: '🇨🇳' },
  de: { label: 'Deutsch', flag: '🇩🇪' },
  ru: { label: 'Русский', flag: '🇷🇺' },
  tr: { label: 'Türkçe', flag: '🇹🇷' },
  ar: { label: 'العربية', flag: '🇸🇦' },
  hi: { label: 'हिन्दी', flag: '🇮🇳' }
};

export class LocalizationStore {
  allTranslations = JSON.parse(JSON.stringify(staticFallbacks));
  isSyncing = false;
  isLoaded = false;
  reportedKeys = new Set();

  constructor(rootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
    this.init();
  }

  getHeaders() {
    return { "Content-Type": "application/json", "Authorization": "Bearer " + SECRET, "X-Api-Key-Prefix": CLIENT };
  }

  async init() {
    await this.loadFromDB();
    this.sync();
  }

  async loadFromDB() {
    try {
      const saved = await db.translations.toArray();
      runInAction(() => {
        saved.forEach((item) => {
          this.allTranslations[item.locale] = { ...this.allTranslations[item.locale], ...item.data };
        });
        this.isLoaded = true;
      });
    } catch (e) {
      console.error("Localization: Failed to load local translations", e);
      runInAction(() => { this.isLoaded = true; });
    }
  }

  async sync() {
    if (this.isSyncing || !SECRET) return;
    runInAction(() => { this.isSyncing = true; });
    try {
      const projectRes = await fetch(API_BASE + "/projects/" + TRAN_PROJECT_ID, { headers: this.getHeaders() });
      if (!projectRes.ok) throw new Error("Project info fetch failed: " + projectRes.status);
      const projectData = await projectRes.json();
      const project = Array.isArray(projectData) ? projectData[0] : projectData;
      const syncMeta = (await db.syncMetadata.get('translation_sync')) || { key: 'translation_sync', localesLastEdit: {} };

      for (const lang of project.languages || []) {
        const code = lang.code;
        const editRes = await fetch(API_BASE + "/check-last-edit/" + TRAN_PROJECT_ID + "?locale=" + code, { headers: this.getHeaders() });
        if (!editRes.ok) continue;
        const remoteUpdatedAt = String((await editRes.json()).updatedAt);
        if (syncMeta.localesLastEdit[code] === remoteUpdatedAt) continue;

        const readRes = await fetch(API_BASE + "/read-all/" + TRAN_PROJECT_ID + "?locale=" + code, { headers: this.getHeaders() });
        if (!readRes.ok) continue;
        const rawData = await readRes.json();
        const newData = {};
        if (Array.isArray(rawData)) rawData.forEach((item) => { if (item.termText) newData[item.termText] = item.translationText; });
        else Object.assign(newData, rawData);

        runInAction(() => { this.allTranslations[code] = { ...this.allTranslations[code], ...newData }; });
        await db.translations.put({ locale: code, data: newData, lastSyncTimestamp: remoteUpdatedAt });
        syncMeta.localesLastEdit[code] = remoteUpdatedAt;
        await db.syncMetadata.put(syncMeta);
      }
    } catch (e) {
      console.warn("Localization: Background sync failed.", e);
    } finally {
      runInAction(() => { this.isSyncing = false; });
    }
  }

  async reportMissingKey(key) {
    if (this.reportedKeys.has(key) || !SECRET) return;
    this.reportedKeys.add(key);
    try {
      await fetch(API_BASE + "/projects/" + TRAN_PROJECT_ID + "/terms", { method: 'POST', headers: this.getHeaders(), body: JSON.stringify({ termText: key }) });
    } catch (e) { /* best effort */ }
  }

  get availableLocales() {
    const codes = Array.from(new Set([...Object.keys(this.allTranslations), ...Object.keys(LOCALE_METADATA)]));
    return codes
      .map((code) => ({ code, label: LOCALE_METADATA[code]?.label || code.toUpperCase(), flag: LOCALE_METADATA[code]?.flag || '🏳️' }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  get currentLocaleTranslations() {
    const lang = this.rootStore.settingsStore.language;
    return this.allTranslations[lang] || this.allTranslations.en || {};
  }

  t(key, params = {}) {
    const data = this.currentLocaleTranslations;
    let value = data[key];
    if (typeof value !== 'string') value = key.split('.').reduce((cur, part) => (cur && cur[part] !== undefined ? cur[part] : undefined), data);
    if (typeof value !== 'string') { this.reportMissingKey(key); return key; }
    return Object.keys(params).reduce((s, p) => s.split('{' + p + '}').join(params[p] ?? '?'), value);
  }
}

